import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import ChampionshipRegistrationModal from '../components/ChampionshipRegistrationModal';
import { Championship, ChampionshipRegistration } from '../types';
import { formatDate, formatDateTime, formatCountdown, parseRoundDateTime } from '../utils/time';
import { SortDirection, getSortIndicator, sortRows } from '../utils/sort';
import './ChampionshipsPage.css';

interface RegistrationSelection {
  eventId: string;
  carName: string;
}

interface RegistrationForm {
  carName: string;
  carNumber: string;
  agreedRules: boolean;
  carSelections?: RegistrationSelection[];
}

interface ChampionshipsPageProps {
  broadcastPlatform?: 'youtube' | 'twitch' | '';
  broadcastChannel?: string;
  displayTimezone?: string;
}

const ChampionshipsPage: React.FC<ChampionshipsPageProps> = ({ broadcastPlatform = '', broadcastChannel = '', displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone }) => {
  const navigate = useNavigate();
  const [championships, setChampionships] = useState<Championship[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationModalChampionship, setRegistrationModalChampionship] = useState<Championship | null>(null);
  const [registrationForm, setRegistrationForm] = useState<RegistrationForm>({ carName: '', carNumber: '', agreedRules: false, carSelections: [] });
  const [registrationMessage, setRegistrationMessage] = useState<string | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [isSubmittingRegistration, setIsSubmittingRegistration] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('ace-public-auth-token');
  });
  const [user, setUser] = useState<{ steamId?: string; firstName?: string; lastName?: string } | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem('ace-public-user-profile');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [cars, setCars] = useState<any[]>([]);
  const [registration, setRegistration] = useState<ChampionshipRegistration | null>(null);
  const [registrations, setRegistrations] = useState<ChampionshipRegistration[]>([]);
  const [registrationCounts, setRegistrationCounts] = useState<Record<string, number>>({});
  const [registrationStates, setRegistrationStates] = useState<Record<string, ChampionshipRegistration | null>>({});
  const [endedWinners, setEndedWinners] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const loadChampionships = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.getChampionships();
        const nextChampionships = res.data.championships || [];
        setChampionships(nextChampionships);
      } catch (err) {
        console.error('Failed to load championships:', err);
        setError('Unable to load championship data.');
      } finally {
        setLoading(false);
      }
    };

    const loadCars = async () => {
      try {
        const res = await api.getCars();
        setCars(res.data || []);
      } catch (err) {
        console.error('Failed to load cars:', err);
      }
    };

    loadChampionships();
    loadCars();
  }, []);

  useEffect(() => {
    const loadRegistrationState = async () => {
      if (!registrationModalChampionship || !authToken) {
        setRegistration(null);
        setRegistrations([]);
        return;
      }

      try {
        const res = await api.getChampionshipRegistrations(registrationModalChampionship.id);
        const nextRegistrations = res.data.registrations || [];
        setRegistrations(nextRegistrations);
        const storedProfile = window.localStorage.getItem('ace-public-user-profile');
        let currentSteamId: string | null = null;
        if (storedProfile) {
          try {
            currentSteamId = JSON.parse(storedProfile).steamId || null;
          } catch {
            currentSteamId = null;
          }
        }
        const mine = nextRegistrations.find((entry: ChampionshipRegistration) => entry.steamId === currentSteamId && entry.status !== 'left') || null;
        setRegistration(mine);
        if (mine) {
          setRegistrationForm({
            carName: mine.carName,
            carNumber: mine.carNumber,
            agreedRules: mine.agreedRules,
            carSelections: mine.carSelections || [],
          });
        }
      } catch (err) {
        console.warn('Unable to load registration state', err);
      }
    };

    loadRegistrationState();
  }, [authToken, registrationModalChampionship]);

  useEffect(() => {
    let active = true;

    const loadRegistrationStates = async () => {
      if (!championships.length) {
        setRegistrationCounts({});
        setRegistrationStates({});
        return;
      }

      const storedProfile = window.localStorage.getItem('ace-public-user-profile');
      let currentSteamId: string | null = null;
      if (storedProfile) {
        try {
          currentSteamId = JSON.parse(storedProfile).steamId || null;
        } catch {
          currentSteamId = null;
        }
      }

      const nextEntries = await Promise.all(championships.map(async (championship) => {
        if (!authToken || !currentSteamId) {
          return [championship.id, null, 0] as const;
        }

        try {
          const res = await api.getChampionshipRegistrations(championship.id);
          const nextRegistrations = res.data.registrations || [];
          const mine = nextRegistrations.find((entry: ChampionshipRegistration) => entry.steamId === currentSteamId && entry.status !== 'left') || null;
          const activeCount = nextRegistrations.filter((entry: ChampionshipRegistration) => entry.status !== 'left').length;
          return [championship.id, mine, activeCount] as const;
        } catch {
          return [championship.id, null, 0] as const;
        }
      }));

      if (!active) return;

      const nextRegistrationStates: Record<string, ChampionshipRegistration | null> = {};
      const nextRegistrationCounts: Record<string, number> = {};
      nextEntries.forEach(([championshipId, registration, count]) => {
        nextRegistrationStates[championshipId] = registration;
        nextRegistrationCounts[championshipId] = count;
      });

      setRegistrationStates(nextRegistrationStates);
      setRegistrationCounts(nextRegistrationCounts);
    };

    void loadRegistrationStates();

    return () => {
      active = false;
    };
  }, [authToken, championships, user?.steamId]);

  const championshipMeta = useMemo(() => championships.map((championship) => {
    const rounds = championship.events.flatMap((event) => event.rounds || []);
    const startDate = rounds.map((round) => round.date).sort()[0];
    const endDate = rounds.map((round) => round.date).sort().pop();
    const now = new Date();
    const hasStarted = startDate ? new Date(`${startDate}T00:00:00`) <= now : false;
    const hasEnded = endDate ? new Date(`${endDate}T23:59:59`) < now : false;
    const state = hasEnded ? 'Ended' : hasStarted ? 'Live' : 'Upcoming';

    const roundWithDate = (round: typeof rounds[number]) =>
      parseRoundDateTime(round.date, round.startTime || '00:00', round.timezone);

    const sortedRounds = [...rounds].sort((left, right) => {
      const leftTime = roundWithDate(left)?.getTime() ?? Number.POSITIVE_INFINITY;
      const rightTime = roundWithDate(right)?.getTime() ?? Number.POSITIVE_INFINITY;
      return leftTime - rightTime;
    });

    const upcomingRound = sortedRounds.find((round) => {
      const roundDate = roundWithDate(round);
      if (!roundDate) return false;
      return roundDate.getTime() >= now.getTime();
    }) || null;

    const nextRoundDateTime = upcomingRound ? roundWithDate(upcomingRound) : null;
    const completedEvents = championship.events.filter((event) => {
      const eventRounds = event.rounds || [];
      if (!eventRounds.length) return false;
      return eventRounds.every((round) => {
        if (!round.date) return false;
        return (roundWithDate(round)?.getTime() ?? 0) < now.getTime();
      });
    }).length;
    const totalEvents = championship.events.length;
    const progressLabel = `${completedEvents}/${totalEvents}`;
    const registrationCount = registrationCounts[championship.id] ?? championship.registrations?.filter((r) => r.status !== 'left').length ?? 0; 
    const userRegistration = registrationStates[championship.id] || null;
    const isRegistrationFull = Boolean(championship.spots && registrationCount >= championship.spots);
    const showNextEventPill = state !== 'Ended' && Boolean(nextRoundDateTime && nextRoundDateTime.getTime() >= now.getTime());

    return {
      championship,
      state,
      startDate,
      endDate,
      upcomingRound,
      nextRoundDateTime,
      progressLabel,
      completedEvents,
      totalEvents,
      registrationCount,
      userRegistration,
      isRegistrationFull,
      showNextEventPill,
      raceCount: rounds.filter((r) => r.raceId).length,
    };
  }), [championships, registrationCounts, registrationStates]);

  // Split into active (live/upcoming) and ended
  const activeChampionshipMeta = useMemo(() => {
    return [...championshipMeta.filter((m) => m.state !== 'Ended')].sort((a, b) => {
      const aTime = a.nextRoundDateTime?.getTime() ?? Number.POSITIVE_INFINITY;
      const bTime = b.nextRoundDateTime?.getTime() ?? Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });
  }, [championshipMeta]);

  const endedChampionshipMeta = useMemo(
    () => championshipMeta.filter((m) => m.state === 'Ended'),
    [championshipMeta]
  );

  const [endedSortConfig, setEndedSortConfig] = useState<{ key: 'name' | 'raceCount' | 'winner' | 'startDate' | 'endDate'; direction: SortDirection }>({ key: 'endDate', direction: 'desc' });

  const handleEndedSort = (key: 'name' | 'raceCount' | 'winner' | 'startDate' | 'endDate') => {
    setEndedSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedEndedChampionships = useMemo(() => sortRows(endedChampionshipMeta, endedSortConfig.direction, (m) => {
    switch (endedSortConfig.key) {
      case 'name': return m.championship.name;
      case 'raceCount': return m.raceCount;
      case 'winner': return endedWinners[m.championship.id] ?? '';
      case 'startDate': return m.startDate ?? '';
      case 'endDate': return m.endDate ?? '';
      default: return '';
    }
  }), [endedChampionshipMeta, endedSortConfig, endedWinners]);

  // Fetch winners for ended championships
  useEffect(() => {
    if (!endedChampionshipMeta.length) return;
    let active = true;

    const fetchWinners = async () => {
      const results = await Promise.all(
        endedChampionshipMeta.map(async ({ championship }) => {
          try {
            const res = await api.getChampionshipWinner(championship.id);
            return [championship.id, res.data.winner ?? null] as const;
          } catch {
            return [championship.id, null] as const;
          }
        })
      );
      if (!active) return;
      const next: Record<string, string | null> = {};
      results.forEach(([id, winner]) => { next[id] = winner; });
      setEndedWinners(next);
    };

    fetchWinners();
    return () => { active = false; };
  }, [endedChampionshipMeta]);

  const openRegistrationModal = (championship: Championship) => {
    const allAllowedCars = Array.from(
      new Set(championship.events.flatMap((event) => event.allowed_cars_list_full || []).map((car) => car.car_name))
    );
    const defaultCarName = championship.sameCarsForAllRaces && allAllowedCars.length === 1 ? allAllowedCars[0] : '';
    const defaultEventSelections = championship.sameCarsForAllRaces
      ? []
      : championship.events.flatMap((event) => {
          const allowedCars = Array.from(
            new Set((event.allowed_cars_list_full || []).map((car) => car.car_name))
          );
          return allowedCars.length === 1 ? [{ eventId: event.id, carName: allowedCars[0] }] : [];
        });

    setRegistrationModalChampionship(championship);
    setRegistrationMessage(null);
    setRegistrationError(null);
    setRegistrationForm({
      carName: defaultCarName,
      carNumber: '',
      agreedRules: false,
      carSelections: defaultEventSelections,
    });
  };

  const handleRegistrationSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!registrationModalChampionship) return;
    if (!authToken) {
      setRegistrationError('Sign in with Discord and link your Steam account first.');
      return;
    }
    if (!registrationForm.agreedRules) {
      setRegistrationError('You must agree to the championship rules.');
      return;
    }

    if (isPerEventSelection) {
      const missing = eventSelections.some(({ eventId, allowedCars }) => {
        const selection = registrationForm.carSelections?.find((item) => item.eventId === eventId);
        return allowedCars.length > 1 && !selection?.carName;
      });
      if (missing) {
        setRegistrationError('Please select a car for every round.');
        return;
      }
    }

    try {
      setIsSubmittingRegistration(true);
      setRegistrationError(null);
      setRegistrationMessage(null);
      const payload = {
        carName: isPerEventSelection
          ? registrationForm.carSelections?.[0]?.carName?.trim() || registrationForm.carName.trim()
          : registrationForm.carName.trim(),
        carNumber: registrationForm.carNumber.trim(),
        agreedRules: registrationForm.agreedRules,
        ...(isPerEventSelection
          ? {
              carSelections: eventSelections
                .map(({ eventId, allowedCars }) => ({
                  eventId,
                  carName: registrationForm.carSelections?.find((item) => item.eventId === eventId)?.carName || (allowedCars.length === 1 ? allowedCars[0] : ''),
                }))
                .filter((selection) => selection.carName),
            }
          : {}),
      };

      if (registration) {
        const res = await api.updateChampionshipRegistration(registrationModalChampionship.id, registration.id, payload, authToken);
        const nextRegistration = res.data.registration || null;
        setRegistration(nextRegistration);
        setRegistrations((prev) => (nextRegistration ? [...prev.filter((entry) => entry.id !== nextRegistration.id), nextRegistration] : prev));
        setRegistrationStates((prev) => ({ ...prev, [registrationModalChampionship.id]: nextRegistration }));
        setRegistrationCounts((prev) => ({ ...prev, [registrationModalChampionship.id]: prev[registrationModalChampionship.id] ?? registrations.length }));
        setRegistrationMessage('Registration updated.');
      } else {
        const res = await api.registerForChampionship(registrationModalChampionship.id, payload, authToken);
        const nextRegistration = res.data.registration || null;
        setRegistration(nextRegistration);
        setRegistrations((prev) => {
          if (!nextRegistration) return prev;
          const exists = prev.some((entry) => entry.id === nextRegistration.id);
          return exists
            ? prev.map((entry) => entry.id === nextRegistration.id ? nextRegistration : entry)
            : [...prev, nextRegistration];
        });
        setRegistrationStates((prev) => ({ ...prev, [registrationModalChampionship.id]: nextRegistration }));
        setRegistrationCounts((prev) => {
          const wasLeft = registrations.some((entry) => entry.id === nextRegistration?.id && entry.status === 'left');
          return wasLeft
            ? prev
            : { ...prev, [registrationModalChampionship.id]: (prev[registrationModalChampionship.id] ?? 0) + 1 };
        });
        setRegistrationMessage('Registration submitted.');
      }
    } catch (err: any) {
      console.error('Failed to submit registration', err);
      setRegistrationError(err.response?.data?.error || 'Unable to submit registration.');
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const handleWithdrawRegistration = async () => {
    if (!registrationModalChampionship || !registration || !authToken) return;
    try {
      setIsSubmittingRegistration(true);
      setRegistrationError(null);
      setRegistrationMessage(null);
      await api.withdrawChampionshipRegistration(registrationModalChampionship.id, registration.id, authToken);
      setRegistration(null);
      setRegistrations((prev) => prev.map((entry) => entry.id === registration.id ? { ...entry, status: 'left' as const } : entry));
      setRegistrationStates((prev) => ({ ...prev, [registrationModalChampionship.id]: null }));
      setRegistrationCounts((prev) => ({ ...prev, [registrationModalChampionship.id]: Math.max((prev[registrationModalChampionship.id] ?? 1) - 1, 0) }));
      setRegistrationForm({ carName: '', carNumber: '', agreedRules: false, carSelections: [] });
      setRegistrationMessage('Registration withdrawn.');
    } catch (err: any) {
      console.error('Failed to withdraw registration', err);
      setRegistrationError(err.response?.data?.error || 'Unable to withdraw registration.');
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const eventSelections = useMemo(() => {
    if (!registrationModalChampionship) return [];

    return registrationModalChampionship.events.map((event, index) => {
      const allowedCars = Array.from(
        new Set((event.allowed_cars_list_full || []).map((car) => car.car_name))
      );
      const firstRound = event.rounds?.[0];
      const label = `Round ${index + 1} - ${firstRound?.track || 'TBD'}${firstRound?.layout ? ` / ${firstRound.layout}` : ''}`;

      return {
        eventId: event.id,
        label,
        allowedCars,
      };
    });
  }, [registrationModalChampionship]);

  const allowedCars = useMemo(() => {
    if (!registrationModalChampionship) return [];
    const values = registrationModalChampionship.events.flatMap((event) => event.allowed_cars_list_full || []).map((car) => car.car_name);
    return Array.from(new Set(values));
  }, [registrationModalChampionship]);

  const isPerEventSelection = Boolean(registrationModalChampionship && !registrationModalChampionship.sameCarsForAllRaces);

  const getEventSelection = (eventId: string) => {
    const existing = registrationForm.carSelections?.find((selection) => selection.eventId === eventId);
    return existing?.carName || '';
  };

  const handleEventSelectionChange = (eventId: string, selectedCar: string) => {
    setRegistrationForm((prev) => {
      const nextSelections = prev.carSelections?.filter((selection) => selection.eventId !== eventId) || [];
      return {
        ...prev,
        carSelections: [...nextSelections, { eventId, carName: selectedCar }],
      };
    });
  };

  return (
    <div>
      <div className="page-header">
        <h1>Championships</h1>
        <p>Browse configured championships and open a championship to view rounds and results.</p>
      </div>

      {loading && <div className="empty-state">Loading championships...</div>}
      {error && <div className="empty-state">{error}</div>}

      {!loading && !error && (
        <>
          {/* Active (live / upcoming) championships as cards */}
          {activeChampionshipMeta.length > 0 && (
            <div className="championships-grid">
              {activeChampionshipMeta.map(({ championship, state, startDate, endDate, upcomingRound, nextRoundDateTime, progressLabel, completedEvents, totalEvents, registrationCount, userRegistration, isRegistrationFull, showNextEventPill }) => (
                <div key={championship.id} className="championship-card" onClick={() => navigate(`/championships/${championship.id}`)}>
                  <div className="championship-card-accent" />
                  <div className="championship-card-top">
                    <h3>{championship.name}</h3>
                    <div className={`status-pill ${state.toLowerCase()}`}>{state}</div>
                    <p className="championship-subtitle">
                      {championship.events.length === 1
                        ? `Running on ${formatDate(startDate ? new Date(`${startDate}T00:00:00`) : null, displayTimezone)}`
                        : `Running ${championship.recurrence} from ${formatDate(startDate ? new Date(`${startDate}T00:00:00`) : null, displayTimezone)} until ${formatDate(endDate ? new Date(`${endDate}T23:59:59`) : null, displayTimezone)}`}
                    </p>
                  </div>

                  <div className="championship-pills">
                    <div className={`info-pill ${isRegistrationFull ? 'is-full' : ''}`}>
                      <span className="pill-label">Signups</span>
                      <span className="players-bulb">
                        <span className="players-bulb-dot" />
                        {championship.registrations?.filter(r => r.status !== 'left').length ?? 0}/{championship.spots ?? 'N/A'}
                      </span>
                    </div>
                    {showNextEventPill && (
                      <div className="info-pill">
                        <span className="pill-label">Next Race</span>
                        <span className="pill-value">{nextRoundDateTime ? formatCountdown(nextRoundDateTime) : 'TBD'}</span>
                      </div>
                    )}
                  </div>

                  <div className="championship-details">
                    <div className="detail-row">
                      <span className="detail-label">Next Race</span>
                      <span className="detail-value">{nextRoundDateTime ? formatDateTime(nextRoundDateTime, displayTimezone) : 'TBD'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Next Track</span>
                      <span className="detail-value">{upcomingRound ? `${upcomingRound.track || 'TBD'} • ${upcomingRound.layout || 'TBD'}` : 'TBD'}</span>
                    </div>
                  </div>

                  <div className="championship-progress">
                    <div className="progress-head">
                      <span>Progress</span>
                      <span>{progressLabel} events</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${Math.max(0, Math.min(100, totalEvents > 0 ? (completedEvents / totalEvents) * 100 : 0))}%` }}
                      />
                    </div>
                  </div>
                  {championship.enableRegistrations && (
                    <div className="championship-actions">
                      {championship.simgridChampionshipId && championship.simgridManaged ? (
                        <a
                          className="championship-button primary"
                          href={
                            championship.registrations?.some((r) => r.steamId === user?.steamId)
                              ? `https://www.thesimgrid.com/championships/${championship.simgridChampionshipId}`
                              : `https://www.thesimgrid.com/user_championships/new?registerable_id=${championship.simgridChampionshipId}&registerable_type=Championship`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {championship.registrations?.some((r) => r.steamId === user?.steamId) ? 'Manage' : 'Register'}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className={`championship-button ${userRegistration ? 'secondary' : 'primary'}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!authToken || !user?.steamId || !user?.firstName || !user?.lastName) {
                              alert('Sign in with Discord, link your Steam account, and fill out your first and last name in profile settings first.');
                              return;
                            }
                            openRegistrationModal(championship);
                          }}
                          disabled={!authToken || !user?.steamId || !user?.firstName || !user?.lastName || (!userRegistration && isRegistrationFull)}
                          title={!authToken || !user?.steamId || !user?.firstName || !user?.lastName ? 'Sign in with Discord, link your Steam account, and fill out your first and last name in profile settings first.' : isRegistrationFull && !userRegistration ? 'This championship is full.' : ''}
                        >
                          {userRegistration ? 'Manage' : isRegistrationFull ? 'Full' : 'Register'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Ended championships as compact table */}
          {endedChampionshipMeta.length > 0 && (
            <div className="section-card" style={{ marginTop: activeChampionshipMeta.length > 0 ? '32px' : 0 }}>
              <h2 style={{ marginBottom: '16px' }}>Past Championships</h2>
              <div className="table-wrapper">
                <table className="past-championships-table">
                  <thead>
                    <tr>
                      <th><button type="button" onClick={() => handleEndedSort('name')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Championship <span aria-hidden="true">{getSortIndicator(endedSortConfig.key, 'name', endedSortConfig.direction)}</span></button></th>
                      <th><button type="button" onClick={() => handleEndedSort('raceCount')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Races <span aria-hidden="true">{getSortIndicator(endedSortConfig.key, 'raceCount', endedSortConfig.direction)}</span></button></th>
                      <th><button type="button" onClick={() => handleEndedSort('winner')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Winner <span aria-hidden="true">{getSortIndicator(endedSortConfig.key, 'winner', endedSortConfig.direction)}</span></button></th>
                      <th><button type="button" onClick={() => handleEndedSort('startDate')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Start <span aria-hidden="true">{getSortIndicator(endedSortConfig.key, 'startDate', endedSortConfig.direction)}</span></button></th>
                      <th><button type="button" onClick={() => handleEndedSort('endDate')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>End <span aria-hidden="true">{getSortIndicator(endedSortConfig.key, 'endDate', endedSortConfig.direction)}</span></button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEndedChampionships.map(({ championship, startDate, endDate, raceCount }) => (
                      <tr
                        key={championship.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/championships/${championship.id}`)}
                      >
                        <td data-label="Championship">
                          <span style={{ fontWeight: 600 }}>{championship.name}</span>
                        </td>
                        <td data-label="Races">
                          <span>{raceCount}</span>
                        </td>
                        <td data-label="Winner">
                          <span>
                            {endedWinners[championship.id] === undefined
                              ? '…'
                              : endedWinners[championship.id] ?? '—'}
                          </span>
                        </td>
                        <td data-label="Start">
                          <span>{startDate ? formatDate(new Date(`${startDate}T00:00:00`), displayTimezone) : '—'}</span>
                        </td>
                        <td data-label="End">
                          <span>{endDate ? formatDate(new Date(`${endDate}T23:59:59`), displayTimezone) : '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {championships.length === 0 && (
            <div className="empty-state">No championships configured yet.</div>
          )}
        </>
      )}

      <ChampionshipRegistrationModal
        open={Boolean(registrationModalChampionship)}
        title={registration ? 'Manage Registration' : 'Register for Championship'}
        subtitle={registrationModalChampionship?.name}
        onClose={() => setRegistrationModalChampionship(null)}
      >
        <form onSubmit={handleRegistrationSubmit} style={{ display: 'grid', gap: '12px' }}>
          {isPerEventSelection ? (
            <div className="championship-registration-table">
              <div className="table-row table-header">
                <div className="table-cell">Round</div>
                <div className="table-cell">Track / Layout</div>
                <div className="table-cell">Car Selection</div>
              </div>
              {eventSelections.map(({ eventId, label, allowedCars }) => {
                const selectedCar = getEventSelection(eventId) || (allowedCars.length === 1 ? allowedCars[0] : '');
                const [roundNumber, trackLayout] = label.split(' - ', 2);
                return (
                  <div className="table-row" key={eventId}>
                    <div className="table-cell">{roundNumber}</div>
                    <div className="table-cell">{trackLayout}</div>
                    <div className="table-cell">
                      <select
                        value={selectedCar}
                        onChange={(event) => handleEventSelectionChange(eventId, event.target.value)}
                        required
                        disabled={allowedCars.length === 1}
                      >
                        {allowedCars.length === 0 ? (
                          <option value="">No cars available</option>
                        ) : (
                          <>
                            <option value="">Select a car</option>
                            {allowedCars.map((carName) => {
                              const carData = cars.find((c) => c.name === carName);
                              const displayName = carData?.display_name || carName;
                              return (
                                <option key={carName} value={carName}>
                                  {displayName}
                                </option>
                              );
                            })}
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              <label style={{ display: 'grid', gap: '4px' }}>
                <span>Car</span>
                <select
                  value={registrationForm.carName}
                  onChange={(event) => setRegistrationForm((prev) => ({ ...prev, carName: event.target.value }))}
                  required
                  disabled={allowedCars.length === 1}
                >
                  {allowedCars.length === 1 ? null : <option value="">Select a car</option>}
                  {allowedCars.map((carName) => {
                    const carData = cars.find((c) => c.name === carName);
                    const displayName = carData?.display_name || carName;
                    return (
                      <option key={carName} value={carName}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
          )}
          <div style={{ display: 'grid', gap: '12px' }}>
            <label style={{ display: 'grid', gap: '4px' }}>
              <span>Car Number</span>
              <input
                value={registrationForm.carNumber}
                onChange={(event) => {
                  const value = event.target.value;
                  const isDuplicate = registrations.some(
                    (reg) => reg.carNumber === value && reg.steamId !== user?.steamId
                  );
                  if (isDuplicate) {
                    setRegistrationError(`Car number ${value} is already taken.`);
                  } else {
                    setRegistrationError(null);
                  }
                  setRegistrationForm((prev) => ({ ...prev, carNumber: value }));
                }}
                placeholder="7"
                required
              />
              {registrationError && registrationError.includes('already taken') && (
                <span style={{ color: 'var(--error-color, #f87171)', fontSize: '0.85rem' }}>{registrationError}</span>
              )}
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={registrationForm.agreedRules} onChange={(event) => setRegistrationForm((prev) => ({ ...prev, agreedRules: event.target.checked }))} />
            <span>I agree to the championship rules.</span>
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="submit" className="small-button" disabled={isSubmittingRegistration}>
              {registration ? 'Update Registration' : 'Register'}
            </button>
            {registration && (
              <button type="button" className="small-button" onClick={handleWithdrawRegistration} disabled={isSubmittingRegistration}>
                Withdraw
              </button>
            )}
          </div>
          {registrationMessage && <p style={{ margin: 0, color: 'var(--success-color, #34d399)' }}>{registrationMessage}</p>}
          {registrationError && <p style={{ margin: 0, color: 'var(--error-color, #f87171)' }}>{registrationError}</p>}
        </form>
      </ChampionshipRegistrationModal>
    </div>
  );
};

export default ChampionshipsPage;
